import type { ContactExtraLink, ContactFieldKey, ContactInfo } from '../types'

export function contactIncludedKey(field: ContactFieldKey): `${ContactFieldKey}Included` {
  return `${field}Included`
}

function classifyContactUrlKind(url: string): 'substack' | 'github' | 'other' {
  const lowered = url.toLowerCase()
  if (lowered.includes('substack.com')) return 'substack'
  if (lowered.includes('github.com') || lowered.includes('gitlab.com')) return 'github'
  return 'other'
}

function ensureContactUrl(url: string): string {
  const trimmed = url.trim()
  if (!trimmed) return ''
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return `https://${trimmed}`
}

/** Move legacy additionalLinks (Substack/GitHub) into first-class fields for the editor. */
export function normalizeContactInfo(info: ContactInfo | null | undefined): ContactInfo {
  if (!info || typeof info !== 'object') return {}
  const out: ContactInfo = { ...info }
  const links = normalizeContactExtraLinks(info.additionalLinks)
  const remaining: ContactExtraLink[] = []

  for (const link of links) {
    const rawUrl = link.url.trim()
    if (!rawUrl) continue
    const normalizedUrl = ensureContactUrl(rawUrl)
    const kind = classifyContactUrlKind(normalizedUrl)
    if (kind === 'substack' && !String(out.substack ?? '').trim()) {
      out.substack = normalizedUrl
      out.substackIncluded = link.included !== false
      continue
    }
    if (kind === 'github' && !String(out.github ?? '').trim()) {
      out.github = normalizedUrl
      out.githubIncluded = link.included !== false
      continue
    }
    remaining.push({ ...link, url: rawUrl })
  }

  if (out.substack) out.substack = ensureContactUrl(out.substack)
  if (out.github) out.github = ensureContactUrl(out.github)
  out.additionalLinks = remaining
  return out
}

/** Whether the field is shown on the resume (checkbox on). Legacy rows without *Included use non-empty value. */
export function isContactFieldIncluded(
  info: ContactInfo | null | undefined,
  field: ContactFieldKey
): boolean {
  if (!info) return false
  const flag = info[contactIncludedKey(field)]
  if (flag === true) return true
  if (flag === false) return false
  const value = info[field]
  return typeof value === 'string' && value.trim().length > 0
}

/** Value for preview/PDF when the field is included; otherwise empty. */
export function contactFieldValue(
  info: ContactInfo | null | undefined,
  field: ContactFieldKey
): string {
  if (!isContactFieldIncluded(info, field)) return ''
  const value = info?.[field]
  return typeof value === 'string' ? value.trim() : ''
}

export function normalizeContactExtraLinks(raw: unknown): ContactExtraLink[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map((item, index): ContactExtraLink | null => {
      if (!item || typeof item !== 'object') return null
      const row = item as Record<string, unknown>
      const url = typeof row.url === 'string' ? row.url.trim() : ''
      const id =
        typeof row.id === 'string' && row.id.trim()
          ? row.id.trim()
          : `contact-link-${index}-${Date.now()}`
      const label = typeof row.label === 'string' ? row.label.trim() : ''
      return {
        id,
        url,
        ...(label ? { label } : {}),
        included: row.included !== false,
      }
    })
    .filter((item): item is ContactExtraLink => item !== null)
}

export function isContactExtraLinkIncluded(link: ContactExtraLink): boolean {
  if (link.included === false) return false
  return link.url.trim().length > 0
}

export function contactExtraLinkValues(info: ContactInfo | null | undefined): string[] {
  return normalizeContactExtraLinks(info?.additionalLinks)
    .filter(isContactExtraLinkIncluded)
    .map((link) => {
      const url = link.url.trim()
      const label = (link.label ?? '').trim()
      return label ? `${label}: ${url}` : url
    })
}

export function createContactExtraLink(): ContactExtraLink {
  return {
    id: `contact-link-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
    url: '',
    included: true,
  }
}

export const OPTIONAL_CONTACT_LINK_FIELDS = ['linkedin', 'substack', 'github'] as const
export type OptionalContactLinkField = (typeof OPTIONAL_CONTACT_LINK_FIELDS)[number]

export function optionalContactLinkEditorKey(
  field: OptionalContactLinkField,
): 'linkedinEditorVisible' | 'substackEditorVisible' | 'githubEditorVisible' {
  return `${field}EditorVisible`
}

/** Whether LinkedIn / Substack / GitHub rows appear in the contact editor. */
export function isOptionalContactLinkEditorVisible(
  info: ContactInfo | null | undefined,
  field: OptionalContactLinkField,
): boolean {
  if (!info) return false
  const flag = info[optionalContactLinkEditorKey(field)]
  if (flag === false) return false
  return true
}

/** First optional link field hidden in the editor (for section + restore order). */
export function firstHiddenOptionalContactLinkField(
  info: ContactInfo | null | undefined,
): OptionalContactLinkField | null {
  if (!info) return OPTIONAL_CONTACT_LINK_FIELDS[0]
  for (const field of OPTIONAL_CONTACT_LINK_FIELDS) {
    if (!isOptionalContactLinkEditorVisible(info, field)) return field
  }
  return null
}

export function contactFullName(info: ContactInfo | null | undefined): string {
  if (!info) return ''
  return (
    [contactFieldValue(info, 'firstName'), contactFieldValue(info, 'middleName'), contactFieldValue(info, 'lastName')]
      .filter(Boolean)
      .join(' ') || ''
  )
}

const CONTACT_DETAIL_BULLET = ' • '

/** International display for resume header / PDF (e.g. +351919191596 → +351 919 191 596). */
export function formatContactPhoneForDisplay(phone: string): string {
  const trimmed = phone.trim()
  if (!trimmed) return ''
  if (trimmed.includes(' ') && trimmed.startsWith('+')) {
    return trimmed.replace(/\s+/g, ' ')
  }
  const compact = trimmed.replace(/[^\d+]/g, '')
  const m = compact.match(/^(\+\d{1,3})(\d+)$/)
  if (!m) return trimmed
  const prefix = m[1]
  const national = m[2]
  if (prefix === '+351' && national.length === 9) {
    return `+351 ${national.slice(0, 3)} ${national.slice(3, 6)} ${national.slice(6)}`
  }
  const groups = national.match(/.{1,3}/g) ?? [national]
  return `${prefix} ${groups.join(' ')}`
}

/** Ordered contact segments for header line (location, phone, email, linkedin, extra links). */
export function contactDetailLineParts(info: ContactInfo | null | undefined): string[] {
  if (!info) return []
  const phone = contactFieldValue(info, 'phone')
  return [
    contactFieldValue(info, 'location'),
    phone ? formatContactPhoneForDisplay(phone) : '',
    contactFieldValue(info, 'email'),
    contactFieldValue(info, 'linkedin'),
    contactFieldValue(info, 'substack'),
    contactFieldValue(info, 'github'),
    ...contactExtraLinkValues(info),
  ].filter(Boolean)
}

/**
 * Plain-text contact line for PDF/DOCX.
 * Zero-width break before each " • segment" so wraps move the bullet with the following
 * item only after earlier segments (substack, then •, then github on overflow).
 */
export function contactDetailLine(info: ContactInfo | null | undefined): string {
  const parts = contactDetailLineParts(info)
  if (parts.length === 0) return ''
  return parts
    .map((part, i) => (i === 0 ? part : `\u200b${CONTACT_DETAIL_BULLET}${part}`))
    .join('')
}
